import { useState, useEffect, useMemo } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Paper,
  Button,
  rem,
  Badge,
  Grid,
  Table,
  Group,
  ThemeIcon,
} from "@mantine/core";
import {
  IconTruckDelivery,
  IconBoxSeam,
  IconCoin,
  IconShoppingBag,
  IconClock,
} from "@tabler/icons-react";
import { useTranslation } from "../context/TranslationContext";
import apiService from "../services/api";

export default function Shipping() {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getPublicShippingTiers()
      .then((data) => { if (!cancelled) setTiers(Array.isArray(data) ? data : []); })
      .catch((err) => { console.error(err); if (!cancelled) setTiers([]); });
    return () => { cancelled = true; };
  }, []);

  // Render each tier as a value range: [min, next higher min) — the top tier
  // is "X and above", the bottom tier starts at CHF 0.00.
  const rows = useMemo(() => {
    const sorted = [...(tiers || [])].sort((a, b) => Number(b.minSubtotal) - Number(a.minSubtotal));
    return sorted.map((tier, i) => {
      const min = Number(tier.minSubtotal);
      const nextHigher = i > 0 ? Number(sorted[i - 1].minSubtotal) : null;
      const range = nextHigher != null
        ? `CHF ${min.toFixed(2)} - CHF ${nextHigher.toFixed(2)}`
        : `CHF ${min.toFixed(2)} ${t("andAbove", "and above")}`;
      return {
        range,
        cost: `CHF ${Number(tier.cost).toFixed(2)}`,
        free: Number(tier.cost) === 0,
      };
    });
  }, [tiers, t]);

  return (
    <Box component="main" pb={rem(120)}>
      {/* 1. HEADER SECTION */}
      <Container size="lg" pt={rem(80)} pb={rem(40)}>
        <Stack gap="xs" align="center">
          <Badge variant="dot" color="blue" size="lg">
            {t("shippingBadge", "Logistics")}
          </Badge>
          <Title
            order={1}
            size={rem(48)}
            ta="center"
            fw={900}
            style={{ letterSpacing: rem(-1) }}
          >
            {t("shippingTitle", "Shipping Information")}
          </Title>
          <Text c="dimmed" size="lg" ta="center" maw={600}>
            {t(
              "shippingTagline",
              "Freshness delivered directly to your doorstep across Switzerland."
            )}
          </Text>
        </Stack>
      </Container>

      <Container size="lg" mt={rem(40)}>
        <Stack gap={rem(100)}>
          {/* 2. WHY NO NEXT DAY? */}
          <Grid gutter={80} align="center">
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Box
                p="xl"
                bg="blue.0"
                style={{
                  borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
                  aspectRatio: "1/1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconTruckDelivery
                  size={120}
                  stroke={1}
                  color="var(--mantine-color-blue-6)"
                  aria-hidden="true"
                  focusable="false"
                />
              </Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Title order={2} size="h1" mb="lg">
                {t("whyNotNextDay", "Why not next-day delivery?")}
              </Title>
              <Stack gap="md">
                <Text size="lg" lh={1.8} c="gray.8">
                  {t("whyNotNextDayPara1")}
                </Text>
                <Text size="lg" lh={1.8} c="gray.8">
                  {t("whyNotNextDayPara2")}
                </Text>
                <Text size="lg" lh={1.8} c="gray.8" fw={500}>
                  {t("whyNotNextDayPara3")}
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>

          {/* 3. DELIVERY TIME */}
          <Grid gutter={80} align="center">
            <Grid.Col span={{ base: 12, md: 7 }} order={{ base: 2, md: 1 }}>
              <Title order={2} size="h1" mb="lg">
                {t("deliveryTimeTitle", "Delivery Times")}
              </Title>
              <Paper withBorder p="xl" radius="md" bg="blue.0" mb="lg">
                <Group wrap="nowrap">
                  <IconClock color="var(--mantine-color-blue-6)" size={30} aria-hidden="true" focusable="false" />
                  <Text size="xl" fw={700} c="blue.9">
                    {t(
                      "deliveryTimeText",
                      "Orders are delivered on Thursdays and Fridays."
                    )}
                  </Text>
                </Group>
              </Paper>
              <Text size="lg" lh={1.8} c="gray.7" italic>
                {t(
                  "deliveryTimeNote",
                  "Please ensure someone is available to receive the fresh products."
                )}
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 5 }} order={{ base: 1, md: 2 }}>
              <Box
                p="xl"
                bg="blue.0"
                style={{
                  borderRadius: "70% 30% 30% 70% / 70% 70% 30% 30%",
                  aspectRatio: "1/1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconBoxSeam
                  size={120}
                  stroke={1}
                  color="var(--mantine-color-blue-6)"
                  aria-hidden="true"
                  focusable="false"
                />
              </Box>
            </Grid.Col>
          </Grid>

          {/* 4. FINAL SECTION: COSTS & ACTION */}
          <Box pt={rem(40)}>
            <Grid gutter={rem(60)} align="stretch">
              {/* Shipping Costs Card (Left) */}
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Paper withBorder p="xl" radius="lg" shadow="sm" h="100%">
                  <Stack gap="xl">
                    <Box>
                      <Group gap="sm" mb="xs">
                        <ThemeIcon
                          size="lg"
                          radius="xl"
                          color="blue"
                          variant="light"
                          aria-hidden="true"
                        >
                          <IconCoin size={20} aria-hidden="true" focusable="false" />
                        </ThemeIcon>
                        <Title order={2} size={rem(28)} fw={800}>
                          {t("shippingCostsTitle", "Shipping Costs")}
                        </Title>
                      </Group>
                      <Text c="dimmed" size="md">
                        {t(
                          "shippingCostsSubtitle",
                          "Costs depend on the order value."
                        )}
                      </Text>
                    </Box>

                    <Table
                      verticalSpacing="md"
                      horizontalSpacing="md"
                      fontSize="lg"
                    >
                      <Table.Caption>
                        {t("shippingCostsTableCaption", "Shipping costs by order value")}
                      </Table.Caption>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th scope="col" fw={700}>
                            {t("orderValue", "Order Value")}
                          </Table.Th>
                          <Table.Th scope="col" ta="right" fw={700}>
                            {t("shippingCost", "Shipping Cost")}
                          </Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {tiers === null ? (
                          <Table.Tr>
                            <Table.Td colSpan={2}>
                              <Text size="sm" c="dimmed" role="status" aria-live="polite">
                                {t("loading", "Loading...")}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        ) : rows.map((row, index) => (
                          <Table.Tr key={index}>
                            <Table.Td fw={500}>{row.range}</Table.Td>
                            <Table.Td ta="right">
                              {row.free ? (
                                <Badge color="green" variant="light" size="lg">
                                  {t("freeShippingBadge", "FREE")}
                                </Badge>
                              ) : (
                                <Text fw={700}>{row.cost}</Text>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Stack>
                </Paper>
              </Grid.Col>

              {/* Ready to Order Expanded Bubble (Right) */}
              <Grid.Col span={{ base: 12, md: 5 }}>
                <Box
                  p={rem(60)}
                  bg="blue.0"
                  h="100%"
                  style={{
                    borderRadius: "60% 40% 40% 60% / 50% 50% 50% 50%", // Slightly more circular but still organic
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    boxShadow: "inset 0 0 40px rgba(0,0,0,0.02)",
                  }}
                >
                  <Stack gap="xl">
                    <Box>
                      <Title order={3} size={rem(42)} fw={900} lh={1.1}>
                        {t("readyToOrderTitle", "Ready to Order?")}
                      </Title>
                      <Text size="lg" c="gray.7" mt="md" lh={1.6} maw={350}>
                        {t(
                          "readyToOrderText",
                          "Experience the freshness of authentic Swiss-made Indian dairy products."
                        )}
                      </Text>
                    </Box>

                    <Button
                      component="a"
                      href="/products"
                      size="xl"
                      radius="xl"
                      color="blue"
                      leftSection={<IconShoppingBag size={22} aria-hidden="true" focusable="false" />}
                      px={rem(50)}
                      h={rem(60)}
                    >
                      {t("shopNow", "Shop Now")}
                    </Button>
                  </Stack>
                </Box>
              </Grid.Col>
            </Grid>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
